import React, { useState, useEffect } from 'react';
import { combatService, CombatSession, CombatParticipant, CombatSkill } from '../services/combatService';
import { karmaService } from '../services/karmaService';
import { motion, AnimatePresence } from 'framer-motion';
import { GameState } from '../types';
import { VisualCombatArena } from './combat/VisualCombatArena';
import { combatTransitionService } from '../services/combatTransitionService';

interface CombatScreenProps {
  gameState: GameState;
  onNavigate: (screen: string) => void;
  onCombatEnd: () => void;
}

export const CombatScreen: React.FC<CombatScreenProps> = ({ 
  gameState, 
  onNavigate,
  onCombatEnd 
}) => {
  const [combatSession, setCombatSession] = useState<CombatSession | null>(null);
  const [selectedSkill, setSelectedSkill] = useState<CombatSkill | null>(null);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [combatLog, setCombatLog] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [damageNumbers, setDamageNumbers] = useState<Array<{ id: string; amount: number; x: number; y: number }>>([]);

  useEffect(() => {
    initializeCombat();
  }, []);

  const initializeCombat = async () => {
    if (!gameState.userProfile?.uid) return;

    try {
      setIsLoading(true);
      setError(null);

      // Check for existing combat session
      let session = await combatService.getPlayerCombatSession(gameState.userProfile.uid);
      
      if (!session) {
        // Start new PvE combat
        session = await combatService.startCombat(
          'pve',
          [gameState.userProfile.uid],
          [
            { name: 'Void Wraith', type: 'npc' as const },
            { name: 'Corrupted AI', type: 'npc' as const }
          ]
        );
        setCombatLog(['Combat initiated! Face your digital demons...']);
      }

      setCombatSession(session);
    } catch (err) {
      setError('Failed to initialize combat');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkillSelect = (skill: CombatSkill) => {
    setSelectedSkill(skill);
    setSelectedTargets([]);
  };

  const handleTargetSelect = (targetId: string) => {
    if (!selectedSkill) return;

    if (selectedTargets.includes(targetId)) {
      setSelectedTargets(selectedTargets.filter(id => id !== targetId));
    } else {
      // For now, single target only
      setSelectedTargets([targetId]);
    }
  };

  const executeAction = async () => {
    if (!combatSession || !selectedSkill || selectedTargets.length === 0) return;
    if (!gameState.userProfile?.uid) return;

    try {
      setIsLoading(true);
      setError(null);

      const results = await combatService.executeAction(
        combatSession.id,
        gameState.userProfile.uid,
        selectedSkill.id,
        selectedTargets
      );

      // Update combat log
      const newLogs = results.map(result => result.message);
      setCombatLog(prev => [...prev, ...newLogs].slice(-10));

      // Add damage numbers for visual feedback
      results.forEach((result, idx) => {
        if (result.damage && result.damage > 0) {
          const damageId = `damage_${Date.now()}_${idx}`;
          const randomX = 300 + Math.random() * 400; // Right side for enemies
          const randomY = 100 + Math.random() * 200;

          setDamageNumbers(prev => [...prev, {
            id: damageId,
            amount: result.damage,
            x: randomX,
            y: randomY
          }]);

          // Remove after animation
          setTimeout(() => {
            setDamageNumbers(prev => prev.filter(d => d.id !== damageId));
          }, 1000);
        }
      });

      // Refresh combat session
      const updatedSession = await combatService.getPlayerCombatSession(gameState.userProfile.uid);
      setCombatSession(updatedSession);

      // Check if combat ended
      if (updatedSession?.status !== 'active') {
        handleCombatEnd(updatedSession);
      }

      // Reset selections
      setSelectedSkill(null);
      setSelectedTargets([]);
    } catch (err: any) {
      setError(err.message || 'Failed to execute action');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCombatEnd = (session: CombatSession | null) => {
    if (!session) return;

    let endMessage = '';
    if (session.status === 'victory') {
      endMessage = 'Victory! Your karma resonates with triumph.';
    } else if (session.status === 'defeat') {
      endMessage = 'Defeat... The void claims another dreamer.';
    } else {
      endMessage = 'Combat concluded in balance.';
    }

    setCombatLog(prev => [...prev, endMessage]);
    
    // Restore graphical mode state if transitioning from graphical mode
    const transitionState = combatTransitionService.getTransitionState();
    if (transitionState) {
      console.log('[CombatScreen] Restoring graphical mode state after combat');
      // State will be restored when returning to graphical mode
    }
    
    // Delay navigation to show end message
    setTimeout(() => {
      onCombatEnd();
    }, 3000);
  };

  const getPlayerParticipant = (): CombatParticipant | undefined => {
    if (!combatSession || !gameState.userProfile?.uid) return undefined;
    return combatSession.participants.find(p => p.id === gameState.userProfile!.uid);
  };

  const getEnemyParticipants = (): CombatParticipant[] => {
    if (!combatSession || !gameState.userProfile?.uid) return [];
    return combatSession.participants.filter(p => p.id !== gameState.userProfile!.uid);
  };

  const renderHealthBar = (current: number, max: number, color: string) => (
    <div className="w-full h-4 bg-gray-800 rounded-full overflow-hidden">
      <motion.div 
        className={`h-full ${color}`}
        initial={{ width: '100%' }}
        animate={{ width: `${(current / max) * 100}%` }}
        transition={{ duration: 0.5 }}
      />
    </div>
  );

  const renderKarmaIndicator = (karma: number) => {
    let color = 'text-gray-400';
    let symbol = '☯';
    
    if (karma > 50) {
      color = 'text-blue-400';
      symbol = '☀';
    } else if (karma > 20) {
      color = 'text-green-400';
      symbol = '✦';
    } else if (karma < -50) {
      color = 'text-red-600';
      symbol = '☠';
    } else if (karma < -20) {
      color = 'text-orange-600';
      symbol = '⚡';
    }

    return (
      <span className={`${color} text-2xl`} title={`Karma: ${karma}`}>
        {symbol}
      </span>
    );
  };

  if (isLoading && !combatSession) {
    return (
      <div className="min-h-screen bg-black text-green-400 p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="text-3xl mb-4 animate-pulse">⚔️</div>
          <p>Initializing combat matrix...</p>
        </div>
      </div>
    );
  }

  const player = getPlayerParticipant();
  const enemies = getEnemyParticipants();
  const isPlayerTurn = combatSession?.currentTurn === gameState.userProfile?.uid;

  return (
    <div className="min-h-screen bg-black text-green-400 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Combat Status Bar */}
        <div className="mb-6 p-4 border border-green-400 rounded-lg bg-black/50">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">COMBAT MATRIX</h2>
            <div className="flex items-center gap-4">
              <span className="text-sm">World State: {combatSession?.worldState || 'balanced'}</span>
              {player && renderKarmaIndicator(player.stats.karma)}
            </div>
          </div>
        </div>

        {/* Visual Combat Arena */}
        {player && (
          <VisualCombatArena
            player={{
              id: player.id,
              name: player.name,
              hp: player.stats.hp,
              maxHp: player.stats.maxHp,
              mana: player.stats.energy,
              maxMana: player.stats.maxEnergy,
              isPlayer: true
            }}
            enemies={enemies.map(enemy => ({
              id: enemy.id,
              name: enemy.name,
              hp: enemy.stats.hp,
              maxHp: enemy.stats.maxHp,
              mana: 0,
              maxMana: 100,
              isPlayer: false
            }))}
            damageNumbers={damageNumbers}
            onEntityClick={handleTargetSelect}
          />
        )}

        {/* Skills Panel */}
        {isPlayerTurn && player && (
          <div className="mb-6 p-4 border border-green-400 rounded-lg bg-black/80">
            <h3 className="text-lg font-bold mb-3">COMBAT SKILLS</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {player.skills.map(skill => {
                const canUse = player.stats.energy >= skill.energyCost && 
                              skill.currentCooldown === 0 &&
                              (!skill.emptinessCost || player.stats.emptiness >= skill.emptinessCost);
                
                return (
                  <motion.button
                    key={skill.id}
                    className={`p-3 border rounded-lg text-left ${
                      selectedSkill?.id === skill.id ? 'border-yellow-400 bg-yellow-400/20' :
                      canUse ? 'border-green-400 hover:bg-green-400/10' : 'border-gray-600 opacity-50'
                    }`}
                    onClick={() => canUse && handleSkillSelect(skill)}
                    disabled={!canUse}
                    whileHover={canUse ? { scale: 1.05 } : {}}
                    whileTap={canUse ? { scale: 0.95 } : {}}
                  >
                    <div className="font-bold text-sm">{skill.name}</div>
                    <div className="text-xs mt-1">{skill.description}</div>
                    <div className="text-xs mt-2 space-y-1">
                      <div>Energy: {skill.energyCost}</div>
                      {skill.karmaCost !== 0 && (
                        <div className={skill.karmaCost > 0 ? 'text-blue-400' : 'text-red-400'}>
                          Karma: {skill.karmaCost > 0 ? '+' : ''}{skill.karmaCost}
                        </div>
                      )}
                      {skill.emptinessCost && (
                        <div className="text-purple-400">Emptiness: {skill.emptinessCost}</div>
                      )}
                      {skill.currentCooldown > 0 && (
                        <div className="text-orange-400">Cooldown: {skill.currentCooldown}</div>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Button */}
        {isPlayerTurn && selectedSkill && selectedTargets.length > 0 && (
          <motion.div 
            className="mb-6 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button
              className="px-8 py-3 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 disabled:opacity-50"
              onClick={executeAction}
              disabled={isLoading}
            >
              EXECUTE {selectedSkill.name.toUpperCase()}
            </button>
          </motion.div>
        )}

        {/* Combat Log */}
        <div className="p-4 border border-green-400 rounded-lg bg-black/80 h-40 overflow-y-auto">
          <h3 className="text-sm font-bold mb-2">COMBAT LOG</h3>
          <div className="space-y-1 text-sm font-mono">
            <AnimatePresence>
              {combatLog.map((log, index) => (
                <motion.div
                  key={`${log}-${index}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="text-green-300"
                >
                  &gt; {log}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <motion.div 
            className="mt-4 p-4 border border-red-400 rounded-lg bg-red-400/10 text-red-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {error}
          </motion.div>
        )}

        {/* Exit Button */}
        <div className="mt-6 text-center">
          <button
            className="px-6 py-2 border border-gray-600 rounded-lg hover:bg-gray-800"
            onClick={() => onCombatEnd()}
          >
            Retreat from Combat
          </button>
        </div>
      </div>
    </div>
  );
};